import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import type { TenantProjectionStore } from '@ovasabi/frontend-kit'
import { parseTimestamp } from '../../lib/timestamp'
import { prototypeStoreRegistry } from '../../generated/prototypeRuntime'
import { isLiveRuntime, mediaUrl } from '../../lib/runtime'

/**
 * Shared types + style atoms for the chef-side surfaces.
 *
 * The chef section (Kitchen, Dispatch, Menus, Menu Builder, Earnings) reads
 * from generated projection stores and writes through `commands.*`. Every page
 * needs the same record shape, the same optimistic-echo helper, and the same
 * small layout primitives — extracting them here keeps each page focused on
 * its own state machine.
 */

export type StoreRecord = Record<string, unknown>
export type ProjectionStore = TenantProjectionStore<StoreRecord>

/**
 * Resolve a generated projection store by registry key (e.g. `menu.chef_menus`).
 * Returns `undefined` when the store isn't registered — callers must guard.
 */
export const getStore = (key: string): ProjectionStore | undefined =>
  prototypeStoreRegistry.get(key) as unknown as ProjectionStore | undefined

/**
 * Echo a record into the local projection store so dummy mode advances UI
 * without a backend round-trip. In live mode the projection lane handles it,
 * so we no-op to avoid double application.
 */
export const echoUpsert = (storeKey: string, record: StoreRecord) => {
  if (isLiveRuntime()) return
  getStore(storeKey)?.upsert(record)
}

/**
 * Optimistic upsert that runs in BOTH runtimes — unlike `echoUpsert`, which
 * no-ops in live so the projection stays the single source of truth. Use this
 * only AFTER a live command has been confirmed (awaited without throwing): the
 * projection can lag a sweep before it re-delivers the row, and until then the
 * UI would otherwise keep showing the pre-write value (the "saved but didn't
 * update" reports). The next projection sweep replaces this with the
 * authoritative row — which matches, since the write already succeeded.
 */
export const confirmedUpsert = (storeKey: string, record: StoreRecord) => {
  getStore(storeKey)?.upsert(record)
}

/**
 * ISO Monday of the week containing `d` (YYYY-MM-DD). Useful when the date
 * could be in the past (e.g. resolving a historical week_start) and we want
 * the literal Monday of that week.
 */
export const isoMonday = (d: Date = new Date()): string => {
  const day = d.getDay() || 7
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day + 1)
  return isoDay(monday)
}

/**
 * ISO Monday on or after today (YYYY-MM-DD). When today *is* Monday, returns
 * today; otherwise advances to the next Monday. This is the right default for
 * forward-looking pickers (weekly menus, scheduled orders) — the within-week
 * Monday from `isoMonday(today)` may be in the past, which is non-selectable
 * once `MinimalCalendar` disables past days.
 */
export const isoUpcomingMonday = (d: Date = new Date()): string => {
  const day = d.getDay() || 7 // 1=Mon … 7=Sun
  const daysToAdd = day === 1 ? 0 : 8 - day
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + daysToAdd)
  return isoDay(monday)
}

/** YYYY-MM-DD for the start of `d`'s local day. */
export const isoDay = (d: Date = new Date()): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Whether the timestamp string falls inside today's local day. */
export const isToday = (ts: string | undefined | null): boolean => {
  if (!ts) return false
  const date = new Date(ts)
  return !Number.isNaN(date.getTime()) && isoDay(date) === isoDay()
}

/** Whether the timestamp string falls inside the next 7 UTC days from now. */
export const isWithinWeek = (ts: string | undefined | null): boolean => {
  if (!ts) return false
  const now = Date.now()
  const then = parseTimestamp(ts)
  if (then === null) return false
  const sevenDays = 7 * 24 * 60 * 60 * 1000
  return then >= now && then - now <= sevenDays
}

/**
 * Normalize a projection enum to its short lowercase form. The live runtime
 * emits proto-style SCREAMING_SNAKE values (`MENU_STATUS_DRAFT`,
 * `DISH_STATUS_SOLD_OUT`) while stubs and optimistic echoes use the short form
 * (`draft`, `sold_out`); comparisons and display must accept both.
 */
export const enumShort = (value: unknown, fallback = ''): string => {
  const raw = String(value ?? '').trim()
  if (!raw) return fallback
  if (!/^[A-Z0-9_]+$/.test(raw)) return raw.toLowerCase()
  return raw.replace(/^(?:[A-Z0-9]+_)*?(?:STATUS|CADENCE|TYPE|STATE)_/, '').toLowerCase()
}

/** Human label for a projection enum: short form with spaces (`sold out`). */
export const enumLabel = (value: unknown, fallback = ''): string =>
  enumShort(value, fallback).replace(/_/g, ' ')

/* ── shared style atoms ──────────────────────────────────────────────── */

export const ChefStyle = {
  Stack: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${minimalVars.space.xs};
  `,
  Row: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    > * { min-width: 0; max-width: 100%; }
    gap: ${minimalVars.space.xs};
  `,
  Ref: styled.span`
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textPrimary};
  `,
  Sub: styled.span`
    display: block;
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.5;
    overflow-wrap: anywhere;
  `,
  Status: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    > * { min-width: 0; max-width: 100%; }
    gap: ${minimalVars.space.xs};
    margin-top: ${minimalVars.space.xs};
  `,
  /** A row whose left side is a fixed-size thumbnail and right side flows. */
  ThumbRow: styled.div`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
  `,
}

/**
 * Square thumbnail. Centered cover-image previews use the same component
 * sized larger; small inline previews share the visual language.
 */
export const ImageThumb = styled.div<{ $src?: string; $size?: number }>`
  width: ${({ $size = 56 }) => `${$size}px`};
  height: ${({ $size = 56 }) => `${$size}px`};
  flex: 0 0 ${({ $size = 56 }) => `${$size}px`};
  border-radius: ${minimalVars.radius.sm};
  background-color: ${minimalVars.color.bgSurface};
  background-image: ${({ $src }) => {
    const resolved = mediaUrl($src, 128)
    return resolved ? `url(${resolved})` : 'none'
  }};
  background-size: cover;
  background-position: center;
  border: 1px solid ${minimalVars.color.borderSubtle};
`
